import React, { SyntheticEvent, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { FormField } from '@/components/Form/FormField'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { FieldSeparator } from '@/components/ui/field'
import { Spinner } from '@/components/ui/spinner'
import { cn } from '@/lib/utils'
import { ForgotPasswordDialog } from '@/routes/Login/ForgotPassword/ForgotPassword'
import { ResetPasswordDialog } from '@/routes/Login/ResetPassword/ResetPassword'
import { appENV, checkWindowLocation } from '@/utils/dom'
import { useUIVersion } from '@/utils/UIVersionContext'
import { LogoIcon, LogoTextIcon } from 'components/Icon'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { EyeIcon, EyeOffIcon } from 'lucide-react'
import { links } from 'utils/featureToggles'
import { FormSubmitValues } from 'utils/formValidation'

const RESET_PASSWORD_PATH = '/reset-password'

export interface LoginFormValues {
  email: string
  password: string
}

const LoginFormUI: React.FC<FormikProps<LoginFormValues> & OuterProps> = (props) => {
  const { isNewUI } = useUIVersion()
  const [resetModal, setResetModal] = useState(false)
  const [isPasswordVisible, setIsPasswordVisible] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const {
    handleSubmit,
    setSubmitting,
    isSubmitting,
    isAddLoading,
    errors,
    isAzureActiveDirectoryEnabled,
  } = props

  const handleSubmitForm = (event: SyntheticEvent) => {
    event.preventDefault()
    setSubmitting(true)
    handleSubmit()
  }

  const ENVBadge = useMemo(() => {
    const isDev =
      checkWindowLocation() === appENV.localhost || checkWindowLocation() === appENV.development
    const isTest = checkWindowLocation() === appENV.test

    if (isDev) {
      return <Badge variant='green'>Dev</Badge>
    }

    if (isTest) {
      return <Badge variant='orange'>Test</Badge>
    }

    return <Badge variant='info'>Production</Badge>
  }, [])

  const isResetPasswordRoute = location.pathname.replace(/\/$/, '') === RESET_PASSWORD_PATH

  return (
    <>
      <ForgotPasswordDialog isOpen={resetModal} onClose={() => setResetModal(false)} />
      <ResetPasswordDialog
        isOpen={isResetPasswordRoute}
        onClose={() => navigate(links.login.url, { replace: true })}
      />
      <Form
        onSubmit={handleSubmitForm}
        className='flex justify-center h-full w-full items-center bg-gray-900'
      >
        <div className='flex flex-col items-stretch gap-4 rounded-4xl shadow-sm p-8 min-w-md bg-background border-border border'>
          <div className='flex flex-col items-center gap-2'>
            <LogoTextIcon width={280} />
            {ENVBadge}
          </div>
          <FormField
            name='email'
            error={errors.email}
            label='E-mail'
            placeholder='Enter email address'
          />
          <FormField
            name='password'
            type={isPasswordVisible ? 'text' : 'password'}
            error={errors.password}
            label='Password'
            placeholder='Enter password'
            rightAddon={
              <Button
                variant='outline'
                type='button'
                size='icon-xs'
                onClick={() => setIsPasswordVisible(!isPasswordVisible)}
              >
                {isPasswordVisible ? <EyeIcon /> : <EyeOffIcon />}
              </Button>
            }
          />
          <Button variant='link' onClick={() => setResetModal(true)}>
            Forgot password?
          </Button>
          <Button
            size='lg'
            type='submit'
            className={cn('w-full', !isNewUI && 'bg-[#c00]! text-white! border-[#981616]!')}
            disabled={isSubmitting}
            data-test='submit'
          >
            Log In
          </Button>
          {isAzureActiveDirectoryEnabled && (
            <React.Fragment>
              <FieldSeparator>Or</FieldSeparator>
              <Button
                variant='outline'
                size='lg'
                type='button'
                disabled={isAddLoading}
                onClick={props.onLoginWithAAD}
                className='w-full'
                data-test='tickmill-button'
              >
                <LogoIcon />
                {isAddLoading ? <Spinner /> : 'Login via Tickmill'}
              </Button>
            </React.Fragment>
          )}
        </div>
      </Form>
    </>
  )
}

interface OuterProps {
  isAddLoading: boolean
  isAzureActiveDirectoryEnabled: boolean

  onSubmit(values: FormSubmitValues<LoginFormValues>): Promise<void>
  onLoginWithAAD(): Promise<void>
}

export const LoginForm = withFormik<OuterProps, LoginFormValues>({
  mapPropsToValues: () => {
    return {
      email: '',
      password: '',
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(values)
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<LoginFormValues> = {}
    if (!values.email) {
      errors.email = 'required'
    }
    if (!values.password) {
      errors.password = 'required'
    }
    return errors
  },
  enableReinitialize: true,
})(LoginFormUI)
